"use client";

import { useEffect, useState } from "react";
import { sendContact, type ContactPayload } from "@/lib/landing-contact";

export type ContactSubject = ContactPayload["subject"];

type Props = {
  open: boolean;
  subject: ContactSubject;
  onClose: () => void;
};

const TITLE: Record<ContactSubject, string> = {
  demo: "Solicita una demo",
  enterprise: "Contacto Enterprise",
  access: "Solicita acceso",
};

const LEDE: Record<ContactSubject, string> = {
  demo: "Estamos en fase de prueba. Déjanos tus datos y te coordinamos una demo personalizada.",
  enterprise:
    "Para cadenas o grupos de restaurantes. Cuéntanos sobre tu operación y te respondemos con una propuesta a medida.",
  access:
    "Cuéntanos sobre tu restaurante y te enviamos los pasos para empezar a usar Orkestria.",
};

export function ContactModal({ open, subject, onClose }: Props) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [locations, setLocations] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<null | "ok" | "error">(null);
  const [errMsg, setErrMsg] = useState<string>("");

  useEffect(() => {
    if (open) {
      setDone(null);
      setErrMsg("");
    }
  }, [open, subject]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setErrMsg("");
    const res = await sendContact({
      subject,
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined,
      company: company.trim() || undefined,
      locations: locations.trim() || undefined,
      message: message.trim() || undefined,
    });
    setSubmitting(false);
    if (res.ok) {
      setDone("ok");
    } else {
      setDone("error");
      setErrMsg(res.error || "Error desconocido");
    }
  };

  const showLocations = subject === "enterprise";

  return (
    <div className="ork-modal-overlay" onClick={onClose}>
      <div
        className="ork-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ork-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="ork-modal-close"
          aria-label="Cerrar"
          onClick={onClose}
        >
          ✕
        </button>
        <h3 id="ork-modal-title" className="ork-modal-title">
          {TITLE[subject]}
        </h3>
        <p className="ork-modal-lede">{LEDE[subject]}</p>

        {done === "ok" ? (
          <div className="ork-modal-success">
            <strong>¡Listo!</strong>
            <span>
              Recibimos tu mensaje. Te respondemos a {email || "tu correo"} muy
              pronto.
            </span>
            <button
              type="button"
              className="ork-btn-primary"
              onClick={onClose}
            >
              Cerrar
            </button>
          </div>
        ) : (
          <form className="ork-modal-form" onSubmit={handleSubmit}>
            <label className="ork-field">
              <span>Nombre</span>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre"
              />
            </label>
            <label className="ork-field">
              <span>Correo</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tu@correo.com"
              />
            </label>
            <div className="ork-field-row">
              <label className="ork-field">
                <span>Teléfono</span>
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+57 ..."
                />
              </label>
              <label className="ork-field">
                <span>Restaurante / Empresa</span>
                <input
                  type="text"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Nombre del negocio"
                />
              </label>
            </div>
            {showLocations && (
              <label className="ork-field">
                <span>Número de locales</span>
                <input
                  type="number"
                  min={1}
                  value={locations}
                  onChange={(e) => setLocations(e.target.value)}
                  placeholder="¿Cuántos locales operas?"
                />
              </label>
            )}
            <label className="ork-field">
              <span>Mensaje (opcional)</span>
              <textarea
                rows={3}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Cuéntanos lo que necesitas"
              />
            </label>

            {done === "error" && (
              <div className="ork-modal-error">
                No pudimos enviar el mensaje. {errMsg}
              </div>
            )}

            <div className="ork-modal-actions">
              <button
                type="button"
                className="ork-btn-ghost"
                onClick={onClose}
                disabled={submitting}
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="ork-btn-primary"
                disabled={submitting}
              >
                {submitting ? "Enviando…" : "Enviar"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
